<template>
	<div class="w-full">
		<div class="relative w-full">
			<!-- Selected Card Display -->
			<div
				v-if="store.user.id"
				@click.stop="toggleCardDropdown"
				class="w-full border bg-ihh-white mb-0 rounded h-auto p-2 text-sm pl-2 flex justify-between items-center cursor-pointer"
				:class="showCardDropdown ? 'rounded-b-none border-b-0' : ''"
			>
				<div class="w-full">
					<div v-if="selectedCard?.last4 && !addingNewCard" class="w-full h-8 flex justify-between items-center space-x-2 pr-2">
						<div class="flex items-center space-x-2">
							<div>
								<img :src="selectedCard.img" class="w-9 bg-ihh-white border rounded p-1" />
							</div>
							<div class="text-xs font-medium">
								{{ selectedCard.brand }} ending in
								{{ selectedCard.last4 }}
							</div>
						</div>
						<div class="text-xs font-medium">Expires {{ selectedCard.expiryMonth }}/{{ selectedCard.expiryYear }}</div>
					</div>

					<div v-else class="font-normal text-ihh-black text-xs flex space-x-4 items-center">
						<div>
							<img
								src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAcElEQVR4nO2WwQnAIBAEByuwidiG/q7/jkLgICGa58lBdmBfPnbRUxeEEDcFaMAALEjDPS6viRZo/NaxCjB8sRJHfezEhLmi+fSxTAFsg3IHiEQBTEeAhhBdQ3I+RGPjd9xTFpKyoZJ1N19WMiH+yQn/4zt6Us80ZwAAAABJRU5ErkJggg=="
								alt="bank-card-back-side--v1"
							/>
						</div>
						<div>Use a different card</div>
					</div>
				</div>

				<!-- Dropdown arrow icon -->
				<div>
					<svg class="" height="24" viewBox="0 0 48 48" width="24" xmlns="http://www.w3.org/2000/svg">
						<path d="M14 20l10 10 10-10z" />
						<path d="M0 0h48v48h-48z" fill="none" />
					</svg>
				</div>
			</div>

			<!-- Card dropdown -->
			<div v-if="showCardDropdown" class="absolute z-10 w-full bg-white border rounded rounded-t-none shadow-lg max-h-60 overflow-y-auto text-xs text-ihh-dark-gray">
				<div
					@click.stop="selectCard(card)"
					v-for="(card, index) in cards"
					:key="index"
					class="flex flex-col justify-center p-2 hover:bg-gray-100 cursor-pointer border min-h-16 hover:cursor-pointer hover:shadow-inner"
				>
					<div class="w-full h-8 flex justify-between items-center space-x-2 pr-2">
						<div class="flex items-center space-x-2">
							<div>
								<img :src="card.img" class="w-9 bg-ihh-white border rounded p-1" />
							</div>
							<div class="text-xs text-ihh-form-label font-medium">{{ card.brand }} ending in {{ card.last4 }}</div>
						</div>
						<div class="text-xs text-ihh-form-label font-medium pr-6">Expires {{ card.expiryMonth }}/{{ card.expiryYear }}</div>
					</div>
				</div>
				<div
					@click.stop="((addingNewCard = true), (showCardDropdown = false))"
					class="flex items-center space-x-4 p-2 hover:bg-gray-100 cursor-pointer border border-t-0 min-h-16"
				>
					<div>
						<img
							src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAcElEQVR4nO2WwQnAIBAEByuwidiG/q7/jkLgICGa58lBdmBfPnbRUxeEEDcFaMAALEjDPS6viRZo/NaxCjB8sRJHfezEhLmi+fSxTAFsg3IHiEQBTEeAhhBdQ3I+RGPjd9xTFpKyoZJ1N19WMiH+yQn/4zt6Us80ZwAAAABJRU5ErkJggg=="
							alt="bank-card-back-side--v1"
						/>
					</div>
					<div class="text-ihh-light-blue font-normal">Use a different card</div>
				</div>
			</div>

			<!-- Add new card -->
			<div v-if="addingNewCard" class="w-full space-y-2 mt-4">
				<!-- Card holder full name -->
				<div class="">
					<label class="block text-xs font-medium text-ihh-form-label">Name on Card</label>
					<input id="name" type="text" v-model="newCard.name" class="block w-full border rounded-md h-10 text-xs pl-3" />
				</div>

				<!-- Card number -->
				<div class="flex flex-col sm:flex-row sm:space-x-4">
					<div class="flex-1">
						<label class="block text-xs font-medium text-ihh-form-label">Credit Card Number</label>
						<div class="relative">
							<input
								id="card-number"
								type="text"
								:value="formattedCardNumber"
								class="block w-full pl-3 pr-10 py-2 border rounded-md h-10 text-sm"
								@input="handleCardInput"
							/>
							<span class="absolute inset-y-0 right-0 flex items-center pr-3">
								<svg height="18" viewBox="0 0 24 24" width="18" xmlns="http://www.w3.org/2000/svg" fill="#999">
									<path
										d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
									></path>
								</svg>
							</span>
						</div>
					</div>
				</div>

				<!-- Expiration date and CVV -->
				<div class="flex flex-col sm:flex-row sm:space-x-4">
					<div class="flex-1">
						<label class="block text-xs font-medium text-ihh-form-label">Expiration</label>
						<input
							id="expiration"
							type="text"
							v-model="newCard.expiry"
							class="block w-full border rounded-md h-10 text-xs font-ihh-bold text-ihh-midnight-gray pl-3"
							placeholder="MM / YY"
							@input="validateAndFormatExpiration"
						/>
					</div>

					<div class="flex-1">
						<label class="block text-xs font-medium text-ihh-form-label mt-2 md:mt-0">CVV</label>
						<div class="relative">
							<input id="cvv" type="text" v-model="newCard.cvv" class="block w-full border rounded-md h-10 text-xs font-ihh-bold text-ihh-midnight-gray pl-3" />
							<span class="absolute inset-y-0 right-0 flex items-center pr-3">
								<svg height="18" viewBox="0 0 24 24" width="18" xmlns="http://www.w3.org/2000/svg" fill="#999">
									<path
										d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
									></path>
								</svg>
							</span>
						</div>
					</div>
				</div>

				<div v-if="store?.user?.id" class="flex flex-col gap-2 text-ihh-form-label text-xs font-normal">
					<label class="flex items-center mt-4">
						<input type="checkbox" v-model="saveNewCard" class="mr-2" />
						<span>Save this card for future transactions</span>
					</label>
					<!-- <label class="flex items-center">
						<input type="checkbox" v-model="setNewCardAsDefault" :disabled="!saveNewCard" class="mr-2" />
						<span>Use this card as the default payment method for future transactions</span>
					</label> -->
				</div>
			</div>

			<!-- Existing card cvv verification input -->
			<div v-if="!showCardDropdown && !addingNewCard" class="text-ihh-form-label font-normal text-xs mt-4">
				<div>
					<label>CVV</label>
					<div class="w-full relative">
						<input id="cvv" type="text" v-model="cardCVV" class="block pl-2 border rounded-md h-10 text-xs font-normal" />
						<span class="absolute inset-x-28 inset-y-0 right-0 flex items-center">
							<svg height="18" viewBox="0 0 24 24" width="18" xmlns="http://www.w3.org/2000/svg" fill="#999">
								<path
									d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
								></path>
							</svg>
						</span>
					</div>
				</div>
				<!-- <label v-if="!selectedCard?.isDefault" class="flex items-center mt-4">
					<input type="checkbox" v-if="selectedCard" v-model="setSelectedCardAsDefault" class="mr-2" />
					<span>Use this card as the default payment method for future transactions</span>
				</label> -->
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import { useCheckoutStore } from '../../stores/store.js'
import { debounce } from 'lodash'

export default {
	name: 'Cards',
	inject: ['store'],
	data() {
		return {
			checkout: useCheckoutStore(),
			// UI variables
			showCardDropdown: false,
			addingNewCard: false,

			// Card Data
			cards: [],
			selectedCard: null,
			cardCVV: '',
			newCard: {
				name: '',
				number: '',
				expiry: '',
				cvv: '',
			},
			saveNewCard: false,
			setNewCardAsDefault: false,
			setSelectedCardAsDefault: false,
			initialLoad: true,
		}
	},
	async mounted() {
		await this.fetchCards()
	},
	methods: {
		async fetchCards() {
			if (!this.store?.user?.id || !this.store?.user?.email) {
				this.addingNewCard = true
				return
			}

			console.log('Fetching Big Commerce payment methods for user: ', this.store.user.bcId)

			try {
				let response = await axios.post(`${this.store.api}/controllers/bigCommerce/checkout/payments/stored`, { customerId: this.store?.user?.bcId })
				if (response?.data?.success) {
					console.log('success fetching saved cards...')
					this.cards = response.data.data.filter((card) => card.type === 'stored_card')
					if (!this.cards.length) {
						this.addingNewCard = true
						return
					}
					this.selectedCard = this.cards[0]
					this.checkout.selectCard(this.selectedCard)
				} else {
					console.log('error fetching stored payment methods...')
				}
			} catch (error) {
				console.error('Error fetching stored payment methods:', error)
				this.addingNewCard = true
			} finally {
				this.initialLoad = false
			}
		},
		toggleCardDropdown() {
			this.showCardDropdown = !this.showCardDropdown
		},
		selectCard(card) {
			this.selectedCard = card
			this.showCardDropdown = false
			this.addingNewCard = false
		},
		handleCardInput(event) {
			let rawValue = event.target.value.replace(/\D/g, '') // Remove non-numeric characters
			this.newCard.number = rawValue // Store the raw number

			// Format the input field immediately
			event.target.value = rawValue.replace(/(\d{4})(?=\d)/g, '$1 ')
		},
		validateAndFormatExpiration(event) {
			let input = event.target.value.replace(/\D/g, '') // Remove non-numeric characters

			if (input.length === 1 && input > '1') {
				input = '0' + input // Add a 0 before the first digit if it's 2-9
			}

			if (input.length > 2) {
				input = input.slice(0, 2) + ' / ' + input.slice(2, 4) // Add a / after the second digit
			}

			event.target.value = input // Update the input field value
			this.newCard.expiry = input // Update the v-model value
		},
	},

	watch: {
		newCard: {
			handler: debounce(function (newVal) {
				this.checkout.setNewCard(newVal)
			}, 300),
			deep: true,
		},
		saveNewCard: debounce(function (newVal) {
			this.checkout.updateSaveNewCard(newVal)
		}, 300),
		setNewCardAsDefault: debounce(function (newVal) {
			this.checkout.updateSetNewCardAsDefault(newVal)
		}, 300),
		cardCVV: debounce(function (newVal) {
			this.checkout.setCardCVV(newVal)
		}, 300),
		selectedCard: debounce(function (newVal) {
			console.log('selectedCard watch running')
			this.checkout.selectCard(newVal)
		}, 300),
		addingNewCard: {
			handler(newVal) {
				this.checkout.updateAddingNewCard(newVal)
			},
		},
		'store.user': {
			handler(newVal) {
				console.log('store.user changed, id:', newVal?.id)
				if (newVal?.id && !this.initialLoad) {
					this.fetchCards()
				}
			},
			deep: true,
			immediate: false,
		},
	},

	computed: {
		formattedCardNumber() {
			// Remove all non-numeric characters and format the number with spaces every 4 digits
			return this.newCard.number.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ')
		},
	},
}
</script>

<style scoped></style>
